class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('#CartDrawer-Overlay')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.syncCouponMilestones();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    const openOffersPanel = this.querySelector('cart-offers [role="dialog"]:not([hidden])');
    if (openOffersPanel) {
      const offers = openOffersPanel.closest('cart-offers');
      if (typeof offers?.closePanel === 'function') offers.closePanel();
      else openOffersPanel.hidden = true;
      return;
    }

    this.stopCelebration();
    clearTimeout(this.milestoneTimeout);
    this.milestoneQueue = [];
    this.milestonePlaying = false;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  syncCouponMilestones() {
    if (!this.classList.contains('active')) return;
    const milestone = this.querySelector('cart-coupon-progress');
    const reached = Number(milestone?.dataset.reached || 0);
    const previous = this.couponMilestoneReached || 0;
    this.couponMilestoneReached = reached;
    this.milestoneQueue ||= [];
    if (reached > previous) {
      for (let level = previous + 1; level <= reached; level++) this.milestoneQueue.push(level);
    } else if (reached < previous) {
      this.milestoneQueue = this.milestoneQueue.filter((level) => level <= reached);
    }
    this.playNextCouponMilestone();
  }

  playNextCouponMilestone() {
    if (this.milestonePlaying || !this.milestoneQueue?.length || !this.classList.contains('active')) return;
    const level = this.milestoneQueue.shift();
    const milestone = this.querySelector('cart-coupon-progress');
    if (!milestone || level > Number(milestone.dataset.reached)) return;
    this.milestonePlaying = true;
    this.playCelebration(milestone);
    this.milestoneTimeout = setTimeout(() => {
      this.milestonePlaying = false;
      this.playNextCouponMilestone();
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 5200);
  }

  stopCelebration() {
    this.celebrationRun = (this.celebrationRun || 0) + 1;
    cancelAnimationFrame(this.celebrationFrame);
    clearTimeout(this.celebrationTimeout);
    this.querySelector('[data-coupon-sparkle]')?.classList.remove('is-active');
  }

  playCelebration(anchor) {
    this.stopCelebration();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const sparkle = this.querySelector('[data-coupon-sparkle]');
    const image = sparkle?.querySelector('[data-coupon-sparkle-image]');
    const drawerInner = this.querySelector('.drawer__inner');
    if (!sparkle || !image || !drawerInner) return;

    const run = this.celebrationRun;
    const source = new URL(image.currentSrc || image.src, document.baseURI);
    source.searchParams.set('replay', `${Date.now()}-${run}`);
    const replayImage = image.cloneNode();
    replayImage.removeAttribute('src');
    image.replaceWith(replayImage);
    replayImage.addEventListener('load', () => {
      if (run !== this.celebrationRun || !sparkle.isConnected || !this.classList.contains('active')) return;
      if (anchor?.isConnected) {
        const anchorBounds = anchor.getBoundingClientRect();
        const drawerBounds = drawerInner.getBoundingClientRect();
        sparkle.style.setProperty('--coupon-sparkle-top', `${anchorBounds.top - drawerBounds.top + anchorBounds.height / 2}px`);
      } else {
        sparkle.style.removeProperty('--coupon-sparkle-top');
      }
      sparkle.classList.add('is-active');
      this.celebrationTimeout = setTimeout(() => sparkle.classList.remove('is-active'), 5100);
    }, { once: true });
    this.celebrationFrame = requestAnimationFrame(() => {
      if (run === this.celebrationRun && sparkle.isConnected && this.classList.contains('active')) {
        replayImage.src = source.href;
      }
    });
  }

  disconnectedCallback() {
    this.stopCelebration();
    clearTimeout(this.milestoneTimeout);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, shouldOpen = true) {
    const renderedDrawer = parsedState.sections?.['cart-drawer']
      ? this.getSectionDOM(parsedState.sections['cart-drawer'], 'cart-drawer')
      : null;
    if (renderedDrawer) this.classList.toggle('is-empty', renderedDrawer.classList.contains('is-empty'));
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (!shouldOpen) return;

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (!customElements.get('cart-coupon-progress')) {
  customElements.define('cart-coupon-progress', class extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('cart-drawer');
      this.updateFrame = requestAnimationFrame(() => this.drawer?.syncCouponMilestones());
      const message = this.querySelector('.cart-coupon-progress__message');
      if (message) {
        this.messageObserver = new ResizeObserver(() => {
          const lineHeight = parseFloat(getComputedStyle(message).lineHeight);
          message.classList.toggle('is-wrapped', message.getBoundingClientRect().height > lineHeight * 1.5);
        });
        this.messageObserver.observe(message);
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.updateFrame);
      this.messageObserver?.disconnect();
      requestAnimationFrame(() => this.drawer?.syncCouponMilestones());
    }
  });
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
